// src/context/ActiveLocationContext.js
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { locationsAPI, getActiveLocationStorageKey } from "../lib/api";
import { useAuth } from "./AuthContext";

const ActiveLocationContext = createContext(null);

export function ActiveLocationProvider({ children }) {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const storageKey = getActiveLocationStorageKey();
  const [activeLocationId, setActiveLocationId] = useState(() => {
    try {
      const value = localStorage.getItem(getActiveLocationStorageKey());
      if (!value || value === "all") return null;
      return value;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const isAbortLike = useCallback((err) => {
    const name = err?.name || err?.cause?.name;
    const msg = String(err?.message || err?.cause?.message || "").toLowerCase();
    const code = err?.code;
    return name === "AbortError" || msg.includes("aborted") || code === "ERR_CANCELED";
  }, []);

  const persistActiveLocationId = useCallback((id) => {
    setActiveLocationId(id || null);
    try {
      if (id) localStorage.setItem(storageKey, id);
      else localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  }, [storageKey]);

  useEffect(() => {
    try {
      const value = localStorage.getItem(storageKey);
      setActiveLocationId(value && value !== "all" ? value : null);
    } catch {
      setActiveLocationId(null);
    }
  }, [storageKey, user?.org_id]);

  const loadLocations = useCallback(async (signal) => {
    setLoading(true);
    try {
      const res = await locationsAPI.list({ signal });
      if (signal?.aborted) return;
      const rows = res.data || [];
      setLocations(rows);

      if (rows.length === 0) {
        persistActiveLocationId(null);
        return;
      }

      if (rows.length === 1) {
        const onlyId = rows[0]?.id || null;
        if (onlyId && activeLocationId !== onlyId) {
          persistActiveLocationId(onlyId);
        }
        return;
      }

      const hasStored = activeLocationId !== null && activeLocationId !== undefined && activeLocationId !== "";
      const exists = hasStored && rows.some((l) => l.id === activeLocationId);
      if (hasStored && !exists) {
        persistActiveLocationId(null);
      }
    } catch (error) {
      // Ignore abort errors (normal cleanup)
      if (signal?.aborted || isAbortLike(error)) return;
      
      // Ignore 401 errors (user not logged in yet)
      if (error?.response?.status === 401) {
        setLocations([]);
        persistActiveLocationId(null);
        return;
      }
      
      console.error("Failed to load locations:", error);
      setLocations([]);
      persistActiveLocationId(null);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [activeLocationId, isAbortLike, persistActiveLocationId]);

  useEffect(() => {
    const ctrl = new AbortController();
    loadLocations(ctrl.signal);
    return () => ctrl.abort();
  }, [loadLocations]);

  const reloadLocations = useCallback(() => loadLocations(), [loadLocations]);

  const activeLocation = useMemo(() => {
    return locations.find((l) => l.id === activeLocationId) || null;
  }, [locations, activeLocationId]);

  const value = useMemo(
    () => ({
      locations,
      activeLocationId,
      activeLocation,
      setActiveLocationId: persistActiveLocationId,
      reloadLocations,
      loading,
      canSelectAll: locations.length > 1,
      hasNoAccessibleLocations:
        !loading &&
        Array.isArray(locations) &&
        locations.length === 0 &&
        !Boolean(user?.all_locations_access),
    }),
    [locations, activeLocationId, activeLocation, loading, persistActiveLocationId, reloadLocations, user?.all_locations_access]
  );

  return (
    <ActiveLocationContext.Provider value={value}>
      {children}
    </ActiveLocationContext.Provider>
  );
}

export function useActiveLocation() {
  const ctx = useContext(ActiveLocationContext);
  if (!ctx) throw new Error("useActiveLocation must be used within ActiveLocationProvider");
  return ctx;
}

export function useOptionalActiveLocation() {
  return useContext(ActiveLocationContext);
}
